import { type ReactNode, useContext, useEffect, useMemo } from "react";

import {
  Alert,
  classNames,
  Link,
  SidebarState,
  UIContext,
} from "@alpacahq/ui/old";

import AmplitudeProvider from "~/AmplitudeProvider";
import {
  useGetElitePerks,
  useGetOwnerDetails,
  useGetTradingAccount,
  useIsCryptoSupported,
} from "~/v2/api/hooks";
import { useGetFPSLStatus } from "~/v2/api/hooks/useGetFPSL";
import useZendeskSupport from "~/v2/api/hooks/useZendeskSupport";
import {
  FeatureFlags,
  useAdvancedTradingDashboardAccess,
  useFeatureEnabled,
} from "~/v2/helpers/featureFlags";
import { getCanTransferCrypto } from "~/v2/pages/dashboard/profile";
import { useAccountContext } from "~/v2/providers/AccountProvider";
import { getAppPathname } from "~/v2/utils/appBasePath";
import { UserActions } from "../../api/auth";
import { Path } from "../../path";
import { isPaperOrOnboarding } from "../../utils";
import { EliteMarketDataModal } from "../EliteMarketDataModal";
import { ErrorBoundary } from "../ErrorBoundary";
import { ProgressBar } from "../onboarding";
import { Sidebar, type SidebarProps } from "../Sidebar/Sidebar";
import { PaperWarningBanner } from "./PaperWarningBanner";
import TopBar from "./topbar";

// experimenting with larger screen size for some pages - ie `/trade/NVDA`
const WIDE_PAGE_MAX_WIDTH = "max-w-[1648px]";
const WIDE_PAGES = [
  "/trade/",
  "/account/orders",
  "/dashboard/overview",
  Path.ROUTE_CRYPTO_TRANSFERS,
  Path.ROUTE_DIGITAL_ASSETS_TRANSFERS,
];

// pages that do not limit screen width whatsoever
const FULL_WIDTH_PAGES = [
  Path.ROUTE_HIGH_YIELD_ENROLL,
  Path.ROUTE_HIGH_YIELD_ENROLL_AGREEMENT,
  Path.ROUTE_HIGH_YIELD_ENROLL_COMPLETED,
  Path.ROUTE_IRA_ONBOARDING,
  Path.ROUTE_MARKET_DATA_SUBSCRIBE,
  Path.ROUTE_PROGRAM_INTEREST,
];

export interface PageProps {
  children: ReactNode;
  noHeader?: boolean;
  disableAppMenu?: boolean;
}

export const Page = (props: PageProps) => {
  const { sidebarState } = useContext(UIContext);

  const { product } = useAccountContext();

  const isPaper = product === "paper";
  const isTokenizedEquityTrading = useFeatureEnabled(
    FeatureFlags.TOKENIZED_EQUITY_TRADING,
  );
  const isAdvancedTradingDashboardEnabled =
    useAdvancedTradingDashboardAccess() === "allowed";
  const isWidePage = WIDE_PAGES.some((path) =>
    getAppPathname().startsWith(path),
  );
  const isUnlimitedWidthPage = FULL_WIDTH_PAGES.some((path) =>
    getAppPathname().startsWith(path),
  );

  const { noHeader, children, disableAppMenu } = props;

  const { account: liveAccount } = useGetTradingAccount();

  // These endpoints are using the live account to determine information
  const { details } = useGetOwnerDetails();
  const { perks } = useGetElitePerks({
    accountID: liveAccount?.id ?? "",
    options: {
      enabled: !!liveAccount?.id && !isPaperOrOnboarding(liveAccount),
    },
  });

  const { status: fpslStatus } = useGetFPSLStatus({
    accountID: liveAccount?.id,
    product: "live",
    options: {
      enabled: !!liveAccount?.id && !isPaperOrOnboarding(liveAccount),
    },
  });

  useZendeskSupport();

  const cryptoTier = liveAccount?.crypto_tier ?? 0;

  // percentage of tier completion, given 8 tiers
  const cryptoTierProgress = (cryptoTier / 8) * 100;

  const { isCryptoSupported } = useIsCryptoSupported();
  const canTransferCrypto = getCanTransferCrypto(
    liveAccount,
    isCryptoSupported,
  );
  const canAccessDigitalAssetTransfers =
    !isPaper && liveAccount?.status === "ACTIVE";
  const showWalletNav =
    isTokenizedEquityTrading && canAccessDigitalAssetTransfers
      ? true
      : isCryptoSupported;

  const sidebarProps: Pick<SidebarProps, "settings" | "items"> = useMemo(
    () => ({
      settings: [
        {
          label: "Profile",
          leftIcon: "Identification",
          href: Path.ROUTE_PROFILE,
        },
        {
          label: "Log Out",
          leftIcon: "LockClosed",
          onClick: () => UserActions.userLogout({ global: true }),
        },
      ],
      items: [
        {
          label: "Home",
          leftIcon: "Home",
          href: Path.ROUTE_DASHBOARD,
        },
        {
          label: "Account",
          leftIcon: "RectangleStack",
          subItems: [
            {
              label: "Positions",
              leftIcon: "ChartPie",
              href: Path.ROUTE_POSITIONS,
            },
            {
              label: "Orders",
              leftIcon: "ClipboardDocumentList",
              href: Path.ROUTE_ORDERS,
            },
            {
              label: "Activities",
              leftIcon: "QueueList",
              href: Path.ROUTE_ACCOUNT_ACTIVITIES,
            },
            {
              label: "Balances",
              leftIcon: "TableCells",
              href: Path.ROUTE_BALANCES,
            },
            !isPaper &&
              fpslStatus?.status === "ACTIVE" && {
                label: "Stock Lending",
                leftIcon: "Star",
                href: Path.ROUTE_PROGRAM_INTEREST,
              },
            {
              label: "Configure",
              leftIcon: "AdjustmentsVertical",
              href: Path.ROUTE_CONFIGURATION,
            },
            !isPaper && {
              label: "Documents",
              leftIcon: "DocumentDuplicate",
              href: Path.ROUTE_DOCUMENTS,
            },
          ],
        },
        {
          label: "Alpaca Connect",
          leftIcon: "Cloud",
          href: Path.ROUTE_ALPACA_CONNECT,
        },
        {
          label: "Plans & Features",
          leftIcon: "Heart",
          href: Path.ROUTE_PLANS_AND_FEATURES,
          subItems: [
            !isPaper && {
              label: "Trading Plan",
              leftIcon: "ClipboardDocumentList",
              href: Path.ROUTE_ALPACA_TRADING_PLAN,
            },
            !details?.is_professional && {
              label: "Market Data",
              leftIcon: "PresentationChartLine",
              href: Path.ROUTE_MARKET_DATA,
            },
            !isPaper &&
              canTransferCrypto && {
                label: "Crypto Fee",
                leftIcon: "ChartBarSquare",
                href: Path.ROUTE_PROFILE_CRYPTO_FEES,
              },
          ],
        },
        !isPaper && {
          label: `Funds${showWalletNav ? " & Wallet" : ""}`,
          leftIcon: "Banknotes",
          subItems: [
            {
              label: "Transfer",
              leftIcon: "BuildingLibrary",
              href: Path.ROUTE_FUNDING,
            },
            {
              label: "History",
              leftIcon: "ArchiveBox",
              href: Path.ROUTE_FUNDING_HISTORY,
            },
            isTokenizedEquityTrading &&
              canAccessDigitalAssetTransfers && {
                label: "Digital Assets Transfers",
                leftIcon: "CurrencyDollar",
                href: Path.ROUTE_DIGITAL_ASSETS_TRANSFERS,
              },
            !isTokenizedEquityTrading &&
              isCryptoSupported && {
                label: "Crypto Transfers",
                leftIcon: "CurrencyDollar",
                href: Path.ROUTE_CRYPTO_TRANSFERS,
              },
          ],
        },
        {
          label: "API",
          leftIcon: "CommandLine",
          href: Path.URL_DOCS,
          newTab: true,
        },
        {
          label: "Community",
          leftIcon: "ChatBubbleLeftRight",
          subItems: [
            {
              label: "Slack",
              leftIcon: "Hashtag",
              href: Path.URL_SLACK,
              newTab: true,
            },
            {
              label: "Forum",
              leftIcon: "Users",
              href: Path.URL_FORUM,
              newTab: true,
            },
          ],
        },
        {
          label: "Support",
          leftIcon: "Lifebuoy",
          href: Path.URL_SUPPORT,
          newTab: true,
        },
        {
          label: "Legal",
          leftIcon: "Scale",
          href: Path.URL_DISCLOSURES,
          newTab: true,
        },
        isAdvancedTradingDashboardEnabled && {
          label: "Pro",
          leftIcon: "Beaker",
          href: Path.ROUTE_PRO,
        },
        ["DEV", "STG"].includes(window.env.ENV) && {
          label: "Devtools",
          leftIcon: "CommandLine",
          href: "/devtools",
        },
      ],
    }),
    [
      isCryptoSupported,
      canTransferCrypto,
      canAccessDigitalAssetTransfers,
      showWalletNav,
      details,
      fpslStatus,
      isTokenizedEquityTrading,
      isPaper,
      isAdvancedTradingDashboardEnabled,
    ],
  );

  useEffect(() => {
    // if defined pass account and details to AmplitudeProvider
    if (details && liveAccount) {
      AmplitudeProvider.setAccount({ liveAccount, details });
    }
  }, [liveAccount, details]);

  return (
    <div id="page-wrapper" className="flex h-full w-full">
      {!disableAppMenu && sidebarState !== SidebarState.HIDDEN && (
        <Sidebar {...sidebarProps} ownerId={liveAccount?.id}>
          {sidebarState === SidebarState.EXPANDED &&
            !isPaper &&
            canTransferCrypto && (
              <div className="rounded-md border border-warm-gray-200 border-b-3 bg-white p-2.5">
                <Link
                  className="w-full rounded-md font-medium text-gray-800 no-underline focus:border-yellow-50 focus:outline-none focus:ring-yellow-50 dark:text-gray-200"
                  to={Path.ROUTE_PROFILE_CRYPTO_FEES}
                >
                  <div className="mb-2 flex items-center justify-between">
                    Crypto Fee Rate
                    <label className="rounded bg-gray-200 p-1 text-xs dark:bg-blue-400 dark:text-gray-800">
                      Level&nbsp; {cryptoTier}
                    </label>
                  </div>
                  <ProgressBar percentage={cryptoTierProgress} />
                </Link>
              </div>
            )}
        </Sidebar>
      )}
      <div
        className={classNames(
          "mx-auto h-screen grow overflow-y-scroll",
          sidebarState !== SidebarState.HIDDEN && "pl-1",
          isWidePage ? WIDE_PAGE_MAX_WIDTH : "max-w-6xl",
          isUnlimitedWidthPage && "max-w-full",
        )}
      >
        {!noHeader && (
          <>
            <TopBar />
            {product === "paper" && <PaperWarningBanner />}
          </>
        )}
        <div>
          <div id="__anchor" />
          <ErrorBoundary
            errorComponent={({ errorMessage }) => (
              <Alert
                variant="danger"
                title="Something went wrong."
                message={`Please refresh the page and try again or contact support. (error: ${errorMessage})`}
              />
            )}
          >
            {children}
          </ErrorBoundary>
        </div>
      </div>
      <EliteMarketDataModal perks={perks} isPaper={isPaper} />
    </div>
  );
};

export default Page;
